/**
 * Hook pour traduire automatiquement des libellés via l'API
 * On garde les textes en français (source) et l'API traduit vers la langue courante.
 * Même en français : on affiche les libellés sources (pas d'appel API).
 */

import { useState, useEffect, useMemo } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { translateBatch } from "../utils/translationAPI";

const SOURCE_LANG = "fr";

/**
 * Retourne les libellés dans la langue courante.
 * - En français : affiche les textes sources tels quels (pas d'API).
 * - En anglais / khmer : traduit via l'API depuis le français.
 * @param {Record<string, string>} labelsMap - Ex: { closeButton: "Fermer", edit: "Modifier" }
 * @returns {Record<string, string>} Même clés, valeurs en français ou traduites
 */
export function useAutoTranslatedLabels(labelsMap) {
  const { language } = useLanguage();
  const keys = useMemo(
    () => (labelsMap ? Object.keys(labelsMap) : []),
    [labelsMap],
  );
  const values = useMemo(
    () => (labelsMap ? Object.values(labelsMap) : []),
    [labelsMap],
  );

  const [translatedValues, setTranslatedValues] = useState(values);

  useEffect(() => {
    if (!labelsMap || keys.length === 0) {
      return;
    }
    // Même en français : on utilise les libellés tels quels (aucun appel API)
    if (language === SOURCE_LANG) {
      setTranslatedValues(values);
      return;
    }
    let cancelled = false;
    translateBatch(values, language, SOURCE_LANG)
      .then((translated) => {
        if (!cancelled) setTranslatedValues(translated);
      })
      .catch(() => {
        if (!cancelled) setTranslatedValues(values);
      });
    return () => {
      cancelled = true;
    };
  }, [language, values, keys.length, labelsMap]);

  return useMemo(() => {
    const result = {};
    keys.forEach((key, i) => {
      result[key] = translatedValues[i] ?? labelsMap[key] ?? "";
    });
    return result;
  }, [keys, translatedValues, labelsMap]);
}
